import React, { useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import PropTypes from 'prop-types';
import { t } from "i18next";
import { formatString } from "../../../helpers/utils";
import type { ChartBaseProps } from "../types/chart-types";

/**
 * Props interface for LivisHorizontalBarHighcharts component
 * Extends ChartBaseProps with horizontal bar specific configuration
 */
interface LivisHorizontalBarChartProps extends ChartBaseProps {
  height?: number;
  stacking?: 'none' | 'normal' | 'percent';
  pointWidth?: number;
  borderRadius?: number;
  showLabels?: boolean;
  showTooltip?: boolean;
  barPadding?: number;
}

/**
 * LivisHorizontalBarHighcharts - A customizable horizontal bar chart component
 * 
 * Features:
 * - Supports single or multiple series data
 * - Configurable stacking modes
 * - Customizable bar styling (width, radius, padding)
 * - Optional labels and tooltips
 * - Custom tooltip with translated labels
 * - Responsive design
 * - Transparent background for dashboard integration
 * 
 * @param props - Component configuration and data
 * @returns Rendered Highcharts horizontal bar chart component
 */
const LivisHorizontalBarHighcharts: React.FC<LivisHorizontalBarChartProps> = ({
  data,
  title,
  subtitle,
  x_title,
  y_title,
  height = 80,
  stacking = 'normal',
  pointWidth = 16,
  borderRadius = 8,
  showLabels = false,
  showTooltip = false,
  barPadding = 4,
  onClickPoint,
  tooltip,
  legend = false,
  multiple = false
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Configure global Highcharts settings
  Highcharts.setOptions({
    lang: {
      thousandsSep: ','
    }
  });

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      height: height,
      backgroundColor: 'transparent',
      margin: [0, 0, 0, 0],
      spacing: [0, 0, 0, 0]
    },

    // Chart titles
    title: { text: title || undefined },
    subtitle: { text: subtitle || undefined },

    // X-axis configuration
    xAxis: {
      categories: [''],
      title: x_title ? { text: x_title, style: { fontSize: '14px', fontWeight: '600' } } : { text: undefined },
      labels: { enabled: showLabels },
      lineWidth: 0,
      gridLineWidth: 0,
    },

    // Y-axis configuration
    yAxis: {
      title: y_title ? { text: y_title, style: { fontSize: '14px', fontWeight: '600' } } : { text: undefined },
      labels: { enabled: showLabels },
      gridLineWidth: 0
    },

    // UI elements
    legend: { enabled: legend },
    
    // Custom tooltip configuration matching DefaultCustomTooltip style
    tooltip: {
      enabled: tooltip || showTooltip,
      useHTML: true,
      backgroundColor: 'var(--livis-recipe-details-part-footer-color)',
      borderWidth: 0,
      borderRadius: 12,
      shadow: false,
      shared: stacking == 'none',
       // Share tooltip for stacked bars
      style: {
        color: '#2d3748',
        borderRadius: 12,
      },
      formatter: function() {
        // Build tooltip HTML matching the DefaultCustomTooltip component structure
        let tooltipHTML = '<div class="livis-dashboard-run-down-time">';
        
        if (this.points && this.points.length > 0) {
          // For shared/stacked tooltips - iterate through all points
          this.points.forEach((point) => {
            const seriesName = point.series.name;
            const value = point.y;
            tooltipHTML += `<p>${t(formatString(seriesName))} : ${value?.toLocaleString()}</p>`;
          });
        } else {
          // For single point tooltip
          const seriesName = this.series.name;
          const value = this.y;
          tooltipHTML += `<p>${t(formatString(seriesName))} : ${value?.toLocaleString()}</p>`;
        }
        
        tooltipHTML += '</div>';
        return tooltipHTML;
      }
    },
    
    credits: { enabled: false },

    // Plot options for chart behavior and styling
    plotOptions: {
      bar: {
        stacking: stacking === 'none' ? undefined : stacking,
        borderWidth: 0,
        borderRadius: borderRadius,
        groupPadding: barPadding,
        pointWidth: pointWidth
      },
      series: {
        point: {
          events: {
            click: function(this: Highcharts.Point) {
              if (onClickPoint) {
                onClickPoint({
                  x: this.x,
                  y: this.y,
                  series: {
                    name: this.series.name,
                    type: this.series.type
                  },
                  point: this
                });
              }
            }
          }
        }
      }
    },

    // Data series
    series: Array.isArray(data) ? data : [],
  };

  return (
    <HighchartsReact
      containerProps={{ style: { width: "100%", height: "100%" } }}
      highcharts={Highcharts}
      options={options}
      ref={chartRef}
    />
  );
};

// PropTypes validation
LivisHorizontalBarHighcharts.propTypes = {
  data: PropTypes.array.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  x_title: PropTypes.string,
  y_title: PropTypes.string,
  height: PropTypes.number,
  stacking: PropTypes.oneOf(['none', 'normal', 'percent']),
  pointWidth: PropTypes.number,
  borderRadius: PropTypes.number,
  showLabels: PropTypes.bool,
  showTooltip: PropTypes.bool,
  barPadding: PropTypes.number,
  onClickPoint: PropTypes.func,
  tooltip: PropTypes.bool,
  legend: PropTypes.bool,
  multiple: PropTypes.bool
};

// Default props
LivisHorizontalBarHighcharts.defaultProps = {
  title: '',
  subtitle: '',
  x_title: '',
  y_title: '',
  height: 80,
  stacking: 'normal',
  pointWidth: 16,
  borderRadius: 8,
  showLabels: false,
  showTooltip: false,
  barPadding: 4,
  tooltip: false,
  legend: false,
  multiple: false
};

export default LivisHorizontalBarHighcharts;